import React, { useState } from "react";
import { FaLock, FaTimes, FaEye, FaEyeSlash, FaKey, FaExclamationCircle, FaCheckCircle } from "react-icons/fa";
import { changePassword } from "../../service/register.service";
import styles from "./ChangePasswordModal.module.css";

const ChangePasswordModal = ({ isOpen, onClose }) => {
    const [form, setForm] = useState({
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
    });
    const [showPass, setShowPass] = useState({
        old: false,
        new: false,
        confirm: false
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [alertMsg, setAlertMsg] = useState(null); // { type: 'error' | 'success', text: string }
    const [isSuccess, setIsSuccess] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setForm({ oldPassword: "", newPassword: "", confirmPassword: "" });
        setErrors({});
        setAlertMsg(null);
        setIsSuccess(false);
        onClose();
    };

    const validate = () => {
        const errs = {};
        if (!form.oldPassword) errs.oldPassword = "Current password is required";
        if (!form.newPassword) {
            errs.newPassword = "New password is required";
        } else if (form.newPassword.length < 6) {
            errs.newPassword = "New password must be at least 6 characters";
        }
        if (form.newPassword !== form.confirmPassword) {
            errs.confirmPassword = "Passwords do not match";
        }
        return errs;
    };

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        setErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
        setAlertMsg(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAlertMsg(null);

        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setSubmitting(true);
        try {
            const res = await changePassword({
                oldPassword: form.oldPassword,
                newPassword: form.newPassword
            });

            if (res?.success) {
                setIsSuccess(true);
            } else {
                setAlertMsg({
                    type: "error",
                    text: res?.message || "Failed to change password. Please check your current password."
                });
            }
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || "Failed to change password. Please verify your current password.";
            setAlertMsg({ type: "error", text: msg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        <FaKey /> Change Password
                    </h3>
                    <button className={styles.closeBtn} onClick={handleClose} aria-label="Close modal">
                        <FaTimes />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {/* In-Modal Alert Banner */}
                    {alertMsg && (
                        <div className={`${styles.alertBanner} ${alertMsg.type === "success" ? styles.alertSuccess : styles.alertError}`}>
                            {alertMsg.type === "success" ? (
                                <FaCheckCircle className={styles.alertIcon} />
                            ) : (
                                <FaExclamationCircle className={styles.alertIcon} />
                            )}
                            <span>{alertMsg.text}</span>
                        </div>
                    )}

                    {isSuccess ? (
                        <div className={styles.successBox}>
                            <div className={styles.successIconLarge}>
                                <FaCheckCircle />
                            </div>
                            <h4 className={styles.successHeading}>Password Changed!</h4>
                            <p className={styles.successText}>
                                Your password has been updated securely. You can now use your new password for future sign-ins.
                            </p>
                            <button
                                type="button"
                                className={styles.submitBtn}
                                style={{ width: "100%" }}
                                onClick={handleClose}
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="text-start">
                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>
                                    Current Password <span className={styles.required}>*</span>
                                </label>
                                <div className={`${styles.inputWrap} ${errors.oldPassword ? styles.inputWrapError : ""}`}>
                                    <span className={styles.icon}><FaLock /></span>
                                    <input
                                        type={showPass.old ? "text" : "password"}
                                        name="oldPassword"
                                        value={form.oldPassword}
                                        onChange={handleChange}
                                        placeholder="Enter your current password"
                                        className={styles.input}
                                        autoComplete="current-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.toggleEye}
                                        onClick={() => setShowPass((p) => ({ ...p, old: !p.old }))}
                                        aria-label="Toggle password visibility"
                                    >
                                        {showPass.old ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                {errors.oldPassword && <span className={styles.errorText}>{errors.oldPassword}</span>}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>
                                    New Password <span className={styles.required}>*</span>
                                </label>
                                <div className={`${styles.inputWrap} ${errors.newPassword ? styles.inputWrapError : ""}`}>
                                    <span className={styles.icon}><FaLock /></span>
                                    <input
                                        type={showPass.new ? "text" : "password"}
                                        name="newPassword"
                                        value={form.newPassword}
                                        onChange={handleChange}
                                        placeholder="Minimum 6 characters"
                                        className={styles.input}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.toggleEye}
                                        onClick={() => setShowPass((p) => ({ ...p, new: !p.new }))}
                                        aria-label="Toggle password visibility"
                                    >
                                        {showPass.new ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                {errors.newPassword && <span className={styles.errorText}>{errors.newPassword}</span>}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>
                                    Confirm New Password <span className={styles.required}>*</span>
                                </label>
                                <div className={`${styles.inputWrap} ${errors.confirmPassword ? styles.inputWrapError : ""}`}>
                                    <span className={styles.icon}><FaLock /></span>
                                    <input
                                        type={showPass.confirm ? "text" : "password"}
                                        name="confirmPassword"
                                        value={form.confirmPassword}
                                        onChange={handleChange}
                                        placeholder="Re-enter new password"
                                        className={styles.input}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className={styles.toggleEye}
                                        onClick={() => setShowPass((p) => ({ ...p, confirm: !p.confirm }))}
                                        aria-label="Toggle password visibility"
                                    >
                                        {showPass.confirm ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                </div>
                                {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
                            </div>

                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.cancelBtn} onClick={handleClose} disabled={submitting}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.submitBtn} disabled={submitting}>
                                    {submitting ? "Updating..." : "Update Password"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChangePasswordModal;
